import React, { useEffect, useMemo, useState } from 'react';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import { useFormik } from 'formik';

// Redux
import {
  useCreateOtherExpenseMutation,
  useGetExpenseByIdQuery,
  useUpdateOtherExpenseMutation
} from '../../../redux/api/otherExpenses/otherExpenseApi';
import {
  CreateExpenseParams,
  OtherExpenseClassificationEnum,
  OtherExpenseClassificationValues,
  OtherExpenseTypeValues,
  SearchOtherExpenseResponse
} from '../../../redux/api/otherExpenses/otherExpense.model';

// Components
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwBackdrop from '../../../components/TwBackdrop/TwBackdrop';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import TwDatePicker from '../../../components/TwDatePicker/TwDatePicker';
import TwDropzone from '../../../components/TwDropzone/TwDropzone';
import TwDocumentViewer from '../../../components/TwDocumentViewer/TwDocumentViewer';
import TwEquipmentSelector from '../../../components/TwEquipmentSelector/TwEquipmentSelector';
import TwDriverSelector from '../../../components/TwDriverSelector/TwDriverSelector';
import { TwSelectOption } from '../../../components/TwSelects/TwSelects';

import { CreateOrEditExpenseSchema } from './formValidations';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import TwTripSelector from '../../../components/TwTripSelector/TwTripSelector';
import useAuth from '../../../providers/Auth/Auth.provider';
import { ExpenseUtils } from '../../../utils/expensesUtils';
import './CreateOrEditExpense.scss';
import {
  useDeleteOtherExpenseAttachmentsMutation,
  useUploadExpenseAttachmentsMutation
} from '../../../redux/api/otherExpenseAttachment/otherExpenseAttachment';
import {
  AttachmentFile,
  OtherExpenseAttachment
} from '../../../redux/api/otherExpenseAttachment/otherExpenseAttachment.model';
import TwUploadedFileCard from '../../../components/TwUploadedFileCard/TwUploadedFileCard';
import { USER_ROLE } from '../../../providers/User/User.model';
import TwScannerExpenses from '../../../components/TwScanner/TwScannerExpenses';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { logRocketTrack } from '../../../utils/logRocketUtils';

interface CreateOrEditOtheExpenseFormProps {
  open: boolean;
  onClose: (changed: boolean) => void;
  otherExpenseId: string | null;
  tripId?: string;
  readableTripId?: string;
  isDriverLink?: boolean;
  driver?: { id: string; name: string };
  token?: string;
  isView?: boolean;
  fromDocManager?: boolean;
  onSuccess?: (newExpense: SearchOtherExpenseResponse) => void;
}

export interface CreateOrEditOtheExpenseFormikValues
  extends Omit<
    CreateExpenseParams['body'],
    'date_from' | 'date_to' | 'amount' | 'equipment_id' | 'driver_id' | 'trip_id'
  > {
  equipment_id: string | undefined;
  driver_id: string | undefined;
  trip_id: string | undefined;
  date_from: Date | null;
  date_to: Date | null;
  amount: number | null;
}

const FrequencyOptions: TwSelectOption[] = [
  { label: 'One-time', value: 'ONE_TIME' },
  { label: 'Weekly', value: 'WEEKLY' }
];

const CreateOrEditOtheExpenseForm: React.FC<CreateOrEditOtheExpenseFormProps> = ({
  open,
  onClose,
  otherExpenseId,
  tripId,
  readableTripId,
  isDriverLink = false,
  driver,
  token,
  isView = false,
  fromDocManager = false,
  onSuccess
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();

  const enableLtl = userData?.tenant?.ltl_enabled || false;

  const [files, setFiles] = useState<AttachmentFile[]>([]);
  const [enableDateTo, setEnableDateTo] = useState<boolean>(false);
  const [selectedEquipment, setSelectedEquipment] = useState<
    TwAutocompleteOption | null | undefined
  >(null);
  const [selectedDriver, setSelectedDriver] = useState<TwAutocompleteOption | null | undefined>(
    null
  );
  const [selectedTrip, setSelectedTrip] = useState<TwAutocompleteOption | null | undefined>(null);
  const [selectedType, setSelectedType] = useState<TwAutocompleteOption | null | undefined>(null);
  const [selectedClassification, setSelectedClassification] = useState<
    TwAutocompleteOption | null | undefined
  >({
    id: OtherExpenseClassificationEnum.COMPANY_CHARGE,
    value: OtherExpenseClassificationEnum.COMPANY_CHARGE,
    label: ExpenseUtils.getClassificationLabel(OtherExpenseClassificationEnum.COMPANY_CHARGE)
  });
  const [isRecurringExpense, setIsRecurringExpense] = useState<boolean>(false);
  const [currentFiles, setCurrentFiles] = useState<OtherExpenseAttachment[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const [docViewOpen, setDocViewOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');

  const [createOtherExpense, { isLoading: isCreatingExpense }] = useCreateOtherExpenseMutation();
  const [updateOtherExpense, { isLoading: isUpdatingExpense }] = useUpdateOtherExpenseMutation();
  const [uploadExpenseAttachments, { isLoading: isUploadingAttachments }] =
    useUploadExpenseAttachmentsMutation();
  const [deleteOtherExpenseAttachments, { isLoading: isDeletingExpense }] =
    useDeleteOtherExpenseAttachmentsMutation();
  const [isTwScannerOpen, setIsTwScannerOpen] = useState<boolean>(false);

  // Media query
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { data: otherExpense, isLoading } = useGetExpenseByIdQuery(
    { id: otherExpenseId, token },
    {
      skip: !otherExpenseId || !open
    }
  );

  const initialValues: CreateOrEditOtheExpenseFormikValues = {
    name: '',
    type: null,
    classification: OtherExpenseClassificationEnum.COMPANY_CHARGE,
    equipment_id: undefined,
    driver_id: userData?.role === 'DRIVER' ? userData.driver.id : undefined,
    trip_id: tripId || undefined,
    date_from: new Date(),
    date_to: null,
    amount: null,
    frequency: 'ONE_TIME'
  };

  // Formik setup
  const {
    values,
    errors,
    handleSubmit,
    resetForm,
    setFieldValue,
    setFieldError,
    validateField,
    setErrors
  } = useFormik<CreateOrEditOtheExpenseFormikValues>({
    initialValues: initialValues,
    onSubmit: async (values: CreateOrEditOtheExpenseFormikValues) => {
      const hasSomeError = await validateErrors(values);

      if (hasSomeError) {
        addNotification({
          message: hasSomeError,
          variant: 'error',
          timeToShow: 3000
        });
      } else {
        handleCreateOrEditOtherExpense({
          ...values,
          amount: values.amount || 0,
          date_from: values.date_from as Date,
          date_to: values.date_to ? (values.date_to as Date) : null,
          equipment_id: values.equipment_id || null,
          driver_id: values.driver_id || null,
          trip_id: values.trip_id || null
        });
      }
    },
    validateOnChange: false,
    validateOnBlur: false
  });

  /**
   * Function to handle the change of the form fields
   * this validates the field by separate and sets the field error if there is any
   * this in order to display one error at a time in notification bar and set error
   * classes in the form
   *
   * @param fieldName
   * @param value
   */
  const handleChange = (fieldName: keyof CreateOrEditOtheExpenseFormikValues, value: any): void => {
    validateField(fieldName)
      .then(() => {
        setFieldError(fieldName, '');
      })
      .catch((error) => {
        addNotification({
          message: error.message,
          variant: 'error',
          timeToShow: 3000
        });
        setFieldError(fieldName, error.message);
      });

    setFieldValue(fieldName, value);
  };

  /**
   * Function to validate form fields, returns a promise with the error message or null
   * This function is used to validate the form fields when the user submits the form
   * this in order to display one error at a time in notification bar
   *
   * @param values
   * @returns
   */
  const validateErrors = async (
    values: CreateOrEditOtheExpenseFormikValues
  ): Promise<string | null> => {
    try {
      await CreateOrEditExpenseSchema.validate(values, { abortEarly: false });
      return null;
    } catch (err: any) {
      const firstError = {
        [err.inner[0].path]: err.inner[0].message
      };
      setErrors(firstError);
      return err.inner[0].message;
    }
  };

  const handleCreateOrEditOtherExpense = async (
    params: CreateExpenseParams['body']
  ): Promise<void> => {
    try {
      addNotification({
        message: isView
          ? 'Creating expense...'
          : otherExpenseId
          ? 'Updating expense...'
          : 'Creating expense...',
        variant: 'loading',
        timeToShow: 10000
      });

      const successMessage = isView
        ? 'New expense created successfully'
        : otherExpenseId
        ? 'Expense updated successfully'
        : 'New expense created successfully';
      const response = otherExpenseId
        ? await updateOtherExpense({ body: { id: otherExpenseId, ...params }, token }).unwrap()
        : await createOtherExpense({ body: params, token }).unwrap();

      if (response.data) {
        onSuccess && onSuccess(response.data as SearchOtherExpenseResponse);
      }
      // Check if there are files to upload and delete
      if (files.length > 0 && filesToDelete.length > 0) {
        handleClose(true);
        Promise.all([
          uploadExpenseAttachments({
            otherExpenseId: response.data.id,
            data: { attachments: files }
          }),
          deleteOtherExpenseAttachments({ ids: filesToDelete, expenseId: response.data.id })
        ])
          .then(() => {
            addNotification({
              message: successMessage,
              variant: 'success',
              timeToShow: 5000
            });
          })
          .catch((error) => {
            addNotification({
              message: error.data.message,
              variant: 'error',
              timeToShow: 3000
            });
          });

        return;
      }

      let showMessage = true;
      if (files.length > 0) {
        showMessage = false;
        handleAddOtherExpenseDocuments(response.data.id);
      }

      if (filesToDelete.length > 0) {
        showMessage = false;
        handleDeleteDocuments(response.data.id);
      }

      if (showMessage) {
        addNotification({
          message: successMessage,
          variant: 'success',
          timeToShow: 5000
        });

        handleClose(true);
      }
    } catch (error: any) {
      addNotification({
        message: error.data.message,
        variant: 'error',
        timeToShow: 3000
      });
    }
  };

  const handleAddOtherExpenseDocuments = (otherExpenseId: string): void => {
    const successMessage = isView
      ? 'New expense created successfully'
      : otherExpenseId
      ? 'Expense updated successfully'
      : 'New expense created successfully';

    handleClose(true);
    uploadExpenseAttachments({
      otherExpenseId: otherExpenseId,
      data: { attachments: files },
      token
    })
      .unwrap()
      .then(() => {
        addNotification({
          message: successMessage,
          variant: 'success',
          timeToShow: 5000
        });
      })
      .catch(() => {
        addNotification({
          message: 'Something went wrong uploading the documents, please try again',
          variant: 'error',
          timeToShow: 3000
        });
      });
  };

  const handleDeleteDocuments = (otherExpenseId: string): void => {
    handleClose(true);
    deleteOtherExpenseAttachments({ ids: filesToDelete, expenseId: otherExpenseId })
      .unwrap()
      .then(() => {
        addNotification({
          message: 'Expense updated successfully',
          variant: 'success',
          timeToShow: 5000
        });
      })
      .catch((error) => {
        addNotification({
          message: 'Something went wrong deleting the documents, please try again',
          variant: 'error',
          timeToShow: 3000
        });
      });
  };

  const onFileUploaded = (files: File[]): void => {
    files.forEach((file) => {
      setFiles((prevFiles) => [...prevFiles, file as AttachmentFile]);
    });
  };

  const handleError = (errors: string[]): void => console.error(errors);

  const handleRemoveFile = (fileName: string): void => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.name !== fileName));
  };

  const handleClose = (changed: boolean): void => {
    setEnableDateTo(false);
    setFiles([]);
    resetForm();
    setSelectedEquipment(null);
    setSelectedDriver(null);
    setSelectedTrip(null);
    setIsRecurringExpense(false);
    setSelectedType(null);
    onClose(changed);
  };

  const handleSetIsRecurringExpense = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.checked;
    setIsRecurringExpense(value);
  };

  useEffect(() => {
    // Set form values if expense is fetched
    if (otherExpense) {
      setFieldValue('id', otherExpense.id);
      setFieldValue('name', otherExpense.name);
      setFieldValue('amount', otherExpense.amount);
      setFieldValue('classification', otherExpense.classification);

      setSelectedClassification({
        id: otherExpense.classification,
        value: otherExpense.classification,
        label: ExpenseUtils.getClassificationLabel(otherExpense.classification)
      });

      if (otherExpense.type) {
        setFieldValue('type', otherExpense.type);
        setSelectedType({
          id: otherExpense.type,
          value: otherExpense.type,
          label: ExpenseUtils.getTypeLabel(otherExpense.type)
        });
      }

      if (otherExpense.equipment) {
        setFieldValue('equipment_id', otherExpense.equipment.id);
        setSelectedEquipment({
          id: otherExpense.equipment.id,
          value: otherExpense.equipment.name,
          label: otherExpense.equipment.name,
          subLabel: otherExpense.equipment.type
        });
      }

      if (otherExpense.driver) {
        setFieldValue('driver_id', otherExpense.driver.id);
        setSelectedDriver({
          id: otherExpense.driver.id,
          value: otherExpense.driver.id,
          label: `${otherExpense.driver.first_name} ${otherExpense.driver.last_name}`
        });
      }

      if (otherExpense.trip) {
        setFieldValue('trip_id', otherExpense.trip.id);
        setSelectedTrip({
          id: otherExpense.trip.id,
          value: otherExpense.trip.id,
          label: String(otherExpense.trip.readable_id)
        });
      }

      setFieldValue('date_from', new Date(otherExpense.date_from));
      setFieldValue('date_to', otherExpense.date_to ? new Date(otherExpense.date_to) : null);

      setCurrentFiles(otherExpense.other_expense_attachments);
      if (otherExpense.date_to) {
        setEnableDateTo(true);
      }
      if (otherExpense.frequency === 'WEEKLY') {
        setIsRecurringExpense(true);
      }
    }

    if (!otherExpenseId) setFieldValue('date_from', new Date());

    if (tripId) {
      setFieldValue('trip_id', tripId);
      setSelectedTrip({
        id: tripId,
        value: tripId,
        label: readableTripId || ''
      });
    }

    if (userData && userData.role && userData.role === USER_ROLE.DRIVER) {
      const driverId = userData.driver.id;
      setFieldValue('driver_id', driverId);
      setSelectedDriver({
        id: driverId,
        value: driverId,
        label: `${userData.first_name} ${userData.last_name}`
      });
    }

    if (isDriverLink && driver) {
      setFieldValue('driver_id', driver.id);
      setSelectedDriver({
        id: driver.id,
        value: driver.id,
        label: driver.name
      });
    }
  }, [otherExpense, otherExpenseId, isDriverLink]);

  useEffect(() => {
    if (values.frequency === 'WEEKLY') {
      setEnableDateTo(true);
      setIsRecurringExpense(true);
    } else {
      setEnableDateTo(false);
      setFieldValue('date_to', null);
    }
  }, [values.frequency]);

  useEffect(() => {
    if (isRecurringExpense) {
      setFieldValue('frequency', 'WEEKLY');
    } else {
      setFieldValue('frequency', 'ONE_TIME');
    }
  }, [isRecurringExpense]);

  const submitter = useMemo(() => {
    if (otherExpense) {
      return `${otherExpense.createdBy?.first_name} ${otherExpense.createdBy?.last_name}` || '';
    }

    if (userData && !isDriverLink) {
      return `${userData.first_name} ${userData.last_name}`;
    }

    if (isDriverLink && driver) {
      return driver.name;
    }

    return '';
  }, [otherExpense, userData, isDriverLink, driver]);

  const inputComboStyles = {
    display: 'flex',
    flexDirection: {
      xs: 'column',
      sm: 'row'
    },
    justifyContent: 'space-around',
    width: '100%',
    gap: '16px'
  };

  const handleDeleteAttachment = (fileId: string): void => {
    // files to delete is only handled when editing an expense
    if (otherExpenseId) setFilesToDelete((prevFiles) => [...prevFiles, fileId]);
    setCurrentFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
  };

  const closeScanner = (documentUploaded: boolean, showNotification = true): void => {
    setIsTwScannerOpen(false);

    if (!documentUploaded) {
      logRocketTrack('Upload', {
        action: 'Upload document scanner on expenses tab',
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      if (showNotification) {
        addNotification({
          message: 'No document uploaded',
          position: 'bottomCenter',
          variant: 'error'
        });
      }
    }
  };

  const handleDocumentsUpload = async (): Promise<void> => {
    isTwScannerOpen && setIsTwScannerOpen(false);
  };

  const handleOpenMobileMenu = (): void => {
    setIsTwScannerOpen(true);
  };

  return (
    <>
      {isLoading ? (
        <TwBackdrop isOpen={isLoading} />
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <TwDialog
            open={open}
            title={isView ? 'Expense details' : otherExpenseId ? 'Edit expense' : 'New expense'}
            fullScreen={matchesMobileOrTablet}
            size="sm"
            onClickClose={(): void => handleClose(false)}
            sx={
              fromDocManager
                ? {
                    zIndex: '1300 !important'
                  }
                : {}
            }
            actions={
              isView
                ? [
                    <TwButton
                      key="1"
                      type={TwButtonType.primary}
                      label="Done"
                      action={(): void => handleClose(false)}
                      fullwidth={true}
                    />
                  ]
                : [
                    <TwButton
                      key="1"
                      type={TwButtonType.secondary}
                      label="Cancel"
                      action={(): void => handleClose(false)}
                    />,
                    <TwButton
                      key="2"
                      type={TwButtonType.primary}
                      label={otherExpenseId ? 'Save' : 'Create'}
                      disabled={
                        isCreatingExpense ||
                        isUpdatingExpense ||
                        isUploadingAttachments ||
                        isDeletingExpense
                      }
                      action={handleSubmit}
                    />
                  ]
            }
          >
            <Box>
              {/* Form fields */}
              <TwField
                id="name"
                name="name"
                value={values.name}
                label="Name *"
                inputType={TwInputTypes.text}
                readOnly={isView}
                onInputChange={(e): any => handleChange('name', e.target.value)}
                error={!!errors.name}
              />

              <Box sx={inputComboStyles}>
                <TwAutocomplete
                  label="Type"
                  name="type"
                  sortList={false}
                  filterOptions={(x: any): any => x}
                  renderOption={(props, option): React.ReactNode => {
                    return <RenderOption key={option.id} props={props} option={option} />;
                  }}
                  optionList={OtherExpenseTypeValues.map((type) => {
                    return {
                      id: type,
                      value: type,
                      label: ExpenseUtils.getTypeLabel(type)
                    };
                  })}
                  value={selectedType || null}
                  onChange={(_, newValue: TwAutocompleteOption | null): void => {
                    const selection = newValue ? ({ ...newValue } as TwAutocompleteOption) : null;
                    setSelectedType(selection);
                    handleChange('type', newValue?.id || '');
                  }}
                  error={!!errors.type}
                  sx={{
                    width: '100%'
                  }}
                  readonly={isView}
                  showEndAdornment={!isView}
                />

                <TwAutocomplete
                  label="Classification *"
                  name="classification"
                  filterOptions={(x: any): any => x}
                  renderOption={(props, option): React.ReactNode => {
                    return <RenderOption key={option.id} props={props} option={option} />;
                  }}
                  optionList={OtherExpenseClassificationValues.map((classification) => {
                    return {
                      id: classification,
                      value: classification,
                      label: ExpenseUtils.getClassificationLabel(classification)
                    };
                  })}
                  value={selectedClassification || null}
                  onChange={(_, newValue: TwAutocompleteOption | null): void => {
                    const selection = newValue ? ({ ...newValue } as TwAutocompleteOption) : null;
                    setSelectedClassification(selection);
                    handleChange('classification', newValue?.id || '');
                  }}
                  error={!!errors.classification}
                  sx={{
                    width: '100%'
                  }}
                  disableClearable
                  readonly={isView}
                  showEndAdornment={!isView}
                />
              </Box>

              <Box sx={inputComboStyles}>
                <TwDriverSelector
                  label="Driver"
                  selectedDriver={selectedDriver}
                  hasError={!!errors.driver_id}
                  onChange={(id: string): void => {
                    if (userData?.role === USER_ROLE.DRIVER) return;

                    handleChange('driver_id', id);
                  }}
                  setSelectedDriver={setSelectedDriver}
                  sx={{
                    width: '100% !important'
                  }}
                  readOnly={isDriverLink || isView || userData?.role === USER_ROLE.DRIVER}
                  disabled={userData?.role === USER_ROLE.DRIVER && !isView}
                />

                <Box sx={{ width: '100%' }}>
                  <TwField
                    id="submitter"
                    name="submitter"
                    value={submitter}
                    label="Submitter"
                    inputType={TwInputTypes.text}
                    sx={{
                      width: '100%'
                    }}
                    readOnly={isView}
                    disabled={!isView}
                  />
                </Box>
              </Box>

              <Box sx={inputComboStyles}>
                <TwTripSelector
                  label="Trip #"
                  selectedTrip={selectedTrip}
                  hasError={!!errors.trip_id}
                  onChange={(id: string): void => {
                    if (tripId) return;

                    handleChange('trip_id', id);
                  }}
                  setSelectedTrip={setSelectedTrip}
                  sx={{
                    width: '100%'
                  }}
                  readOnly={isView}
                  disabled={!!tripId}
                  enableLtl={enableLtl}
                />

                <TwEquipmentSelector
                  label="Equipment"
                  isEquipmentPreselected={false}
                  selectedEquipment={selectedEquipment || null}
                  hasError={!!errors.equipment_id}
                  onChange={(id: string): void => {
                    handleChange('equipment_id', id);
                  }}
                  setSelectedEquipment={setSelectedEquipment}
                  isDriverLink={isDriverLink}
                  sx={{
                    width: '100%'
                  }}
                  readOnly={isView}
                  isDriver={userData?.role === USER_ROLE.DRIVER}
                  token={token}
                />
              </Box>

              {/* Checkbox to mark expense as recurring */}
              {/* Hidden in TW-5469 */}
              {/* <Box className="">
                <TwCheckbox
                  name="isRecurringExpense"
                  label={`This is a recurring expense`}
                  checked={isRecurringExpense}
                  onInputChange={handleSetIsRecurringExpense}
                />
              </Box> */}

              <Box
                sx={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: {
                    xs: 'space-between',
                    sm: 'flex-start'
                  }
                }}
              >
                <Box
                  sx={{
                    width: {
                      xs: enableDateTo ? '100%' : '9.5rem',
                      sm: '9.5rem'
                    }
                  }}
                >
                  <TwDatePicker
                    id="dateFrom"
                    name="date_from"
                    label={`${values.frequency === 'WEEKLY' ? 'Date from*' : 'Date*'}`}
                    containerClassName=""
                    value={values.date_from}
                    placeholder="MM/DD/YY"
                    onChange={(newDate): void => {
                      const date = newDate as Date | null;
                      handleChange('date_from', date);
                    }}
                    onInputChange={(value): void => {
                      handleChange('date_from', value);
                    }}
                    error={!!errors.date_from}
                    format="MM/dd/yy"
                    readOnly={isView}
                  />
                </Box>

                {enableDateTo && (
                  <Box
                    sx={{
                      width: {
                        xs: '100%',
                        sm: '9.5rem'
                      }
                    }}
                  >
                    <TwDatePicker
                      id="dateTo"
                      name="date_to"
                      label="Date to*"
                      containerClassName=""
                      value={values.date_to}
                      placeholder="MM/DD/YY"
                      onChange={(newDate): void => {
                        const date = newDate as Date | null;
                        handleChange('date_to', date);
                      }}
                      onInputChange={(value): void => {
                        handleChange('date_to', value);
                      }}
                      error={!!errors.date_to}
                      format="MM/dd/yy"
                      readOnly={isView}
                    />
                  </Box>
                )}

                <TwField
                  id="amount"
                  name="amount"
                  value={values.amount || ''}
                  label="Amount*"
                  inputType={TwInputTypes.number}
                  onInputChange={(e): any => handleChange('amount', e.target.value)}
                  startAdorment={
                    <span style={{ color: '#9CA3AF' }} className="">
                      $
                    </span>
                  }
                  sx={{
                    width: '7rem'
                  }}
                  error={!!errors.amount}
                  readOnly={isView}
                />
              </Box>

              {/* Hidden in TW-5469 */}
              {/* <Box
                sx={{
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row'
                  },
                  justifyContent: 'flex-start',
                  gap: '1rem'
                }}
              >
                <TwSelect
                  label="Frequency"
                  name={'frequency'}
                  optionList={FrequencyOptions}
                  value={values.frequency}
                  onInputChange={(e): void => handleChange('frequency', e.target.value)}
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '49%'
                    },
                    mb: {
                      xs: '1rem',
                      sm: 0
                    }
                  }}
                  disabled={isRecurringExpense}
                />
              </Box> */}

              <Box className="attachments">
                <Typography variant="body1" className="other-expense-modal__attachments__title">
                  Attachments
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="attachments-subtitle"
                  sx={{ marginBottom: '14px' }}
                >
                  Receipts, proof of purchase, etc.
                </Typography>
                {!isView && !matchesMobileOrTablet && (
                  <TwDropzone
                    maxSize={10000000}
                    maxFiles={5}
                    text="Drop file here or"
                    secondaryText="Files Supported: PDF, XSLS, JPG, PNG, maximum size 100MB"
                    highlightedText="Click to upload"
                    onFileUploaded={onFileUploaded}
                    onErrors={handleError}
                    acceptedFiletypes={['application/pdf', 'image/png', 'text/csv', 'image/jpeg']}
                  />
                )}

                {matchesMobileOrTablet && (
                  <TwButton
                    key="1"
                    type={TwButtonType.secondary}
                    label="Upload attachment"
                    action={handleOpenMobileMenu}
                    startIcon={
                      <CloudUploadIcon className="other-expense-modal__attachments__icon" />
                    }
                    fullwidth={true}
                  />
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    maxHeight: '160px',
                    overflow: 'auto',
                    marginTop: '8px'
                  }}
                >
                  {/* files to upload */}
                  {!isView && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        marginTop: '8px'
                      }}
                    >
                      {files.map((file, index) => (
                        <TwUploadedFileCard
                          key={String(index)}
                          file={file}
                          handleRemoveFile={(): void => handleRemoveFile(file.name)}
                          handleClick={(): void => undefined}
                          toUpload
                        />
                      ))}
                    </Box>
                  )}

                  {/* uploaded files */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px'
                    }}
                  >
                    {currentFiles.map((file) => (
                      <TwUploadedFileCard
                        key={file.id}
                        file={file.attachment}
                        createdBy={`${file.createdBy?.first_name} ${file.createdBy?.last_name}`}
                        handleRemoveFile={(): void => handleDeleteAttachment(file.id)}
                        handleClick={(): void => {
                          setDocViewOpen(true);
                          setSelectedDocumentId(file.attachment.id);
                        }}
                        toUpload={false}
                        removableFile={!isView}
                      />
                    ))}
                  </Box>
                </Box>

                <TwDocumentViewer
                  readableId={undefined}
                  selectedFileId={selectedDocumentId}
                  open={docViewOpen}
                  files={[...(currentFiles.map((file) => file.attachment) ?? [])]}
                  onClickClose={function (): void {
                    setDocViewOpen(false);
                  }}
                />
              </Box>
            </Box>
          </TwDialog>

          {matchesMobileOrTablet && (
            <TwScannerExpenses
              isOpen={isTwScannerOpen}
              closeMenu={(): void => setIsTwScannerOpen(false)}
              onClose={closeScanner}
              onUploadSuccess={handleDocumentsUpload}
              onFileUploaded={onFileUploaded}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default CreateOrEditOtheExpenseForm;
